import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as moment from 'moment';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { WebPushService } from 'src/app/services/app/web-push/web-push.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';

@UntilDestroy()
@Component({
  selector: 'app-notification-modal',
  templateUrl: './notification-modal.component.html',
  styleUrls: ['./notification-modal.component.css']
})
export class NotificationModalComponent implements OnInit {
  notifications: any = [];
  category = '';
  categoryTabs = [
    { value: '', label: 'All' },
    { value: 'ticket', label: 'Tickets' },
    { value: 'other', label: 'Others' },
  ];
  @Output() closeModal = new EventEmitter<any>();
  user: any;
  currentDate: any = [];
  timeZone: any;
  unreadCount = 0;
  isPushLoading = false;
  isPushSubscribed = false;
  permissionStatus = 'default';
  isPushSupported = true;

  constructor(
    private sharedDataService: SharedDataService,
    private router: Router,
    public webPushService: WebPushService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.getUser();
    this.sharedDataService.refreshUnreadNotificationCount();
    this.sharedDataService
      .getUnreadNotificationCount()
      .pipe(untilDestroyed(this))
      .subscribe(count => {
        this.unreadCount = count;
        this.cdr.detectChanges();
      });

    this.webPushService.syncSubscriptionStatus().then(() => {
      this.cdr.detectChanges();
    });

    this.webPushService.isSubscribed$
      .pipe(untilDestroyed(this))
      .subscribe(sub => {
        this.isPushSubscribed = sub;
        this.cdr.detectChanges();
      });

    this.webPushService.permissionStatus$
      .pipe(untilDestroyed(this))
      .subscribe(status => {
        this.permissionStatus = status;
        this.cdr.detectChanges();
      });

    this.webPushService.isLoading$
      .pipe(untilDestroyed(this))
      .subscribe(loading => {
        this.isPushLoading = loading;
        this.cdr.detectChanges();
      });

    this.webPushService.isSupported$
      .pipe(untilDestroyed(this))
      .subscribe(supported => {
        this.isPushSupported = supported;
        this.cdr.detectChanges();
      });
  }

  async toggleWebPush() {
    this.isPushLoading = true;
    this.cdr.detectChanges();

    try {
      if (this.isPushSubscribed) {
        const res = await this.webPushService.unsubscribe();
        this.toastService.show(res.message);
      } else {
        const res = await this.webPushService.requestPermissionAndSubscribe();
        this.toastService.show(res.message);
      }
    } finally {
      this.isPushLoading = false;
      this.cdr.detectChanges();
    }
  }

  sendTestPush() {
    this.isPushLoading = true;
    this.webPushService.sendTestNotification().subscribe({
      next: () => {
        this.isPushLoading = false;
        this.toastService.show('Test notification sent! Check your desktop notifications.');
      },
      error: (err) => {
        this.isPushLoading = false;
        this.toastService.show(err?.error?.message || 'Failed to dispatch test notification.');
      }
    });
  }

  onMarkAllAsRead() {
    this.sharedDataService.markAllAsRead().subscribe({
      next: () => {
        if (Array.isArray(this.notifications)) {
          this.notifications.forEach((n: any) => n.isRead = true);
        }
        this.unreadCount = 0;
        this.sharedDataService.refreshUnreadNotificationCount(true);
        this.toastService.show('All notifications marked as read.');
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastService.show('Failed to mark all as read.');
      }
    });
  }

  hasUnreadNotifications(): boolean {
    return Array.isArray(this.notifications) && this.notifications.some((n: any) => !n.isRead);
  }

  getUser() {
    this.sharedDataService.getUser().pipe(untilDestroyed(this)).subscribe((data) => {
      if (data) {
        this.user = data;
        this.timeZone = data.timeZone;
        this.currentDate = [moment().startOf('day').tz(this.timeZone).toISOString(), moment().endOf('day').tz(this.timeZone).toISOString()];
        this.getNotificationsList();
      }
    });
  }

  onSelectCategory(category: string) {
    this.category = category;
    this.getNotificationsList();
  }

  onCloseModal() {
    this.closeModal.emit();
  }

  getNotificationsList() {
    const queryParams: any = {
      sortBy: 'createdAt:desc',
      time: this.currentDate
    };
    if (this.category) {
      queryParams.category = this.category;
    }
    this.sharedDataService
      .getNotifications(queryParams)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.notifications = data.results;
        }
      });
  }

  onNotificationClick(notification: any) {
    this.markAsRead(notification);
    // a support notification opens its ticket or chat
    if (notification.type === 'support' && notification.metadata?.url) {
      this.router.navigateByUrl(notification.metadata.url);
      this.closeModal.emit();
      return;
    }
    if (notification.type === 'trip') {
      this.router.navigate(['trips'], { fragment: notification?.trip?.status });
    }
    this.closeModal.emit();
  }

  onMarkSingleAsRead(event: MouseEvent, notification: any) {
    event.stopPropagation();
    this.markAsRead(notification);
  }

  markAsRead(notification: any) {
    if (!notification || notification.isRead) {
      return;
    }
    notification.isRead = true;
    if (this.unreadCount > 0) {
      this.unreadCount -= 1;
    }
    this.sharedDataService.decrementUnreadNotificationCount(1);
    this.cdr?.detectChanges();

    this.sharedDataService
      .markNotificationAsRead(notification.id || notification._id)
      .subscribe({
        next: () => {},
        error: () => {}
      });
  }

  getRelativeTime(date: any): string {
    return date ? moment(date).fromNow() : '';
  }
}
